import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { getAnnouncementImageUrl } from '../../utils/imageUrl';
import { splitFrenchDateTime } from '../../utils/formatDateTimeFr';
import { isDeliveryOfferMessage } from '../../utils/deliveryOfferMessage';
import { isDeliveryPaymentMessage } from '../../utils/colissimoPaymentMessage';
import { isDeliveryRestitutionElsewhereMessage } from '../../utils/deliveryRestitutionElsewhereMessage';
import DeliveryOfferMessageBody from '../DeliveryOfferMessageBody';
import DeliveryPaymentReceivedMessageBody from '../DeliveryPaymentReceivedMessageBody';
import type { DeliveryPricingResponse } from '../../utils/deliveryPricing';
import type { FoundObjectStatus } from '../../types/foundObject';
import {
  ConversationTheme,
  conversationListItemSx,
  getConversationTheme,
} from '../../utils/conversationTheme';
import {
  getAppleMessageBubbleSx,
  getMessageGroupPosition,
  getMessageGroupSpacing,
} from '../../utils/messageBubbleShape';
import ZoomableChatImage from './ZoomableChatImage';

/** Même convention que sherlook-frontend-next (messages). */
export const IMAGE_MESSAGE_PREFIX = '[[image]]';

/** Fond du fil de discussion (sert aussi à masquer/galber la pointe des bulles). */
export const CONVERSATION_CHAT_BACKGROUND = '#F8F9FB';

function parseImageMessage(content: string): { imagePath: string; caption: string } | null {
  if (!content || typeof content !== 'string' || !content.startsWith(IMAGE_MESSAGE_PREFIX)) return null;
  const payload = content.slice(IMAGE_MESSAGE_PREFIX.length);
  const [rawPath, ...rest] = payload.split('\n');
  const imagePath = rawPath.trim();
  if (!imagePath) return null;
  const caption = rest.join('\n').trim();
  return { imagePath, caption };
}

function formatMessageSenderName(
  sender?: { first_name?: string | null; last_name?: string | null } | null
): string {
  const full = [sender?.first_name, sender?.last_name].filter(Boolean).join(' ').trim();
  return full || 'Le propriétaire';
}

function ChatMessageBody({
  content,
  outgoing,
  sender,
  deliveryPricing,
  deliveryLocationProviderId,
  foundObjectStatus,
  onMarkInTransit,
  onMarkToShip,
  statusActionPending,
}: {
  content: string;
  outgoing?: boolean;
  sender?: { first_name?: string | null; last_name?: string | null } | null;
  deliveryPricing?: DeliveryPricingResponse | null;
  deliveryLocationProviderId?: number;
  foundObjectStatus?: FoundObjectStatus | null;
  onMarkInTransit?: () => Promise<void> | void;
  onMarkToShip?: () => Promise<void> | void;
  statusActionPending?: boolean;
}) {
  const parsed = parseImageMessage(content);
  const captionColor = outgoing ? 'rgba(255,255,255,0.95)' : undefined;
  if (parsed) {
    const src = getAnnouncementImageUrl(parsed.imagePath);
    return (
      <>
        {parsed.caption ? (
          <Typography
            component="div"
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', mb: 0.75, color: captionColor }}
          >
            {parsed.caption}
          </Typography>
        ) : null}
        <ZoomableChatImage
          src={src}
          alt={parsed.caption || 'Image'}
          outgoing={outgoing}
        />
      </>
    );
  }
  if (isDeliveryOfferMessage(content)) {
    return (
      <DeliveryOfferMessageBody
        content={content}
        outgoing={outgoing}
        pricing={deliveryPricing}
        locationProviderId={deliveryLocationProviderId}
      />
    );
  }
  if (isDeliveryPaymentMessage(content)) {
    return (
      <DeliveryPaymentReceivedMessageBody
        content={content}
        payerName={formatMessageSenderName(sender)}
        outgoing={outgoing}
        foundObjectStatus={foundObjectStatus}
        onMarkInTransit={onMarkInTransit}
        onMarkToShip={onMarkToShip}
        statusActionPending={statusActionPending}
      />
    );
  }
  if (isDeliveryRestitutionElsewhereMessage(content)) {
    return (
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          lineHeight: 1.45,
          fontStyle: 'italic',
          ...(outgoing ? { color: 'rgba(255,255,255,0.95)' } : {}),
        }}
      >
        Sherlook — Cet objet a été restitué à son propriétaire.
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'pre-wrap',
        lineHeight: 1.45,
        ...(outgoing ? { color: 'rgba(255,255,255,0.95)' } : {}),
      }}
    >
      {content}
    </Typography>
  );
}

function formatMessageTime(createdAt?: string | null): string {
  if (!createdAt) return '';
  // L'API renvoie « Y-m-d H:i:s » sans offset, déjà en heure serveur (Europe/Paris).
  // Forcer « Z » (UTC) provoquait un décalage +2h en été.
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}/.test(createdAt);
  if (!hasZone) {
    const wallClock = createdAt.match(/(?:^|T|\s)(\d{2}):(\d{2})(?::\d{2})?/);
    if (wallClock) {
      return `${wallClock[1]}:${wallClock[2]}`;
    }
  }
  const normalized = createdAt.includes('T') ? createdAt : createdAt.replace(' ', 'T');
  const d = new Date(normalized);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  return splitFrenchDateTime(createdAt)?.time ?? createdAt;
}

function messageDayKey(createdAt?: string | null): string | null {
  if (!createdAt) return null;
  const match = String(createdAt).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match?.[1]) return match[1];
  const normalized = createdAt.includes('T') ? createdAt : createdAt.replace(' ', 'T');
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDaySeparatorLabel(dayKey: string): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (dayKey === localDayKey(today)) return "Aujourd'hui";
  if (dayKey === localDayKey(yesterday)) return 'Hier';

  const [year, month, day] = dayKey.split('-').map(Number);
  if (!year || !month || !day) return dayKey;
  const date = new Date(year, month - 1, day);
  const label = date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function MessageDaySeparator({ label }: { label: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        my: 1.75,
        px: 1,
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          px: 1.5,
          py: 0.4,
          borderRadius: 999,
          bgcolor: 'rgba(15, 23, 42, 0.06)',
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: 0.2,
          fontSize: '0.75rem',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

type ConversationMessage = {
  id: number;
  content: string;
  created_at?: string | null;
  is_from_me?: boolean;
  is_edited?: boolean;
  edited_at?: string | null;
  is_deleted?: boolean;
  deleted_at?: string | null;
  can_edit?: boolean;
  can_delete?: boolean;
  sender?: {
    first_name?: string | null;
    last_name?: string | null;
  } | null;
};

export function ConversationMessageBubble({
  message,
  theme,
  groupPosition,
  deliveryPricing,
  deliveryLocationProviderId,
  foundObjectStatus,
  onMarkInTransit,
  onMarkToShip,
  statusActionPending,
  onEditMessage,
  onDeleteMessage,
  messageActionsPending,
}: {
  message: ConversationMessage;
  theme: ConversationTheme;
  groupPosition: ReturnType<typeof getMessageGroupPosition>;
  deliveryPricing?: DeliveryPricingResponse | null;
  deliveryLocationProviderId?: number;
  foundObjectStatus?: FoundObjectStatus | null;
  onMarkInTransit?: () => Promise<void> | void;
  onMarkToShip?: () => Promise<void> | void;
  statusActionPending?: boolean;
  onEditMessage?: (messageId: number, content: string) => Promise<void> | void;
  onDeleteMessage?: (messageId: number) => Promise<void> | void;
  messageActionsPending?: boolean;
}) {
  const outgoing = !!message.is_from_me;
  const deleted = !!message.is_deleted;
  const timeLabel = formatMessageTime(message.created_at);
  const bubbleBg = deleted
    ? 'rgba(148, 163, 184, 0.22)'
    : outgoing
      ? theme.bubbleOutgoing
      : theme.bubbleIncoming;
  const showTimestamp = groupPosition === 'single' || groupPosition === 'last';
  const canEdit = !!message.can_edit && !!onEditMessage && !deleted;
  const canDelete = !!message.can_delete && !!onDeleteMessage && !deleted;
  const showActions = outgoing && (canEdit || canDelete);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(message.content);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleSaveEdit = async () => {
    const next = editText.trim();
    if (!next || next === message.content || !onEditMessage) {
      setEditing(false);
      return;
    }
    await onEditMessage(message.id, next);
    setEditing(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: outgoing ? 'flex-end' : 'flex-start',
        mb: getMessageGroupSpacing(groupPosition),
        width: '100%',
        position: 'relative',
        '&:hover .message-actions': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'row',
          gap: 0.25,
          maxWidth: '100%',
          justifyContent: outgoing ? 'flex-end' : 'flex-start',
        }}
      >
        {showActions && !outgoing ? (
          <IconButton
            className="message-actions"
            size="small"
            aria-label="Actions message"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={messageActionsPending}
            sx={{
              opacity: { xs: 1, md: 0 },
              mt: 0.5,
              color: 'text.secondary',
              transition: 'opacity 0.15s ease',
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ) : null}
        <Box
          sx={{
            maxWidth: { xs: '88%', sm: '72%' },
            px: 1.75,
            py: 1,
            mx: outgoing ? 1 : 1.25,
            color: deleted
              ? 'text.secondary'
              : outgoing
                ? theme.bubbleOutgoingText
                : theme.bubbleIncomingText,
            boxShadow: outgoing && !deleted ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            overflow: 'visible',
            ...getAppleMessageBubbleSx(outgoing, bubbleBg, groupPosition, CONVERSATION_CHAT_BACKGROUND),
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            {deleted ? (
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.45 }}
              >
                Message supprimé
              </Typography>
            ) : editing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: { xs: 200, sm: 260 } }}>
                <TextField
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  multiline
                  minRows={2}
                  maxRows={6}
                  size="small"
                  fullWidth
                  autoFocus
                  disabled={messageActionsPending}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: outgoing ? 'rgba(255,255,255,0.92)' : 'background.paper',
                      color: 'text.primary',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditing(false);
                      setEditText(message.content);
                    }}
                    disabled={messageActionsPending}
                  >
                    Annuler
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => void handleSaveEdit()}
                    disabled={messageActionsPending || !editText.trim()}
                  >
                    Enregistrer
                  </Button>
                </Box>
              </Box>
            ) : (
              <ChatMessageBody
                content={message.content}
                outgoing={outgoing}
                sender={message.sender}
                deliveryPricing={deliveryPricing}
                deliveryLocationProviderId={deliveryLocationProviderId}
                foundObjectStatus={foundObjectStatus}
                onMarkInTransit={onMarkInTransit}
                onMarkToShip={onMarkToShip}
                statusActionPending={statusActionPending}
              />
            )}
          </Box>
        </Box>
        {showActions && outgoing ? (
          <IconButton
            className="message-actions"
            size="small"
            aria-label="Actions message"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={messageActionsPending}
            sx={{
              opacity: { xs: 1, md: 0 },
              mt: 0.5,
              color: 'text.secondary',
              transition: 'opacity 0.15s ease',
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
      {(showTimestamp && timeLabel) || message.is_edited ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 0.5,
            px: 1,
            fontSize: '0.72rem',
          }}
        >
          {showTimestamp && timeLabel ? timeLabel : ''}
          {message.is_edited ? `${showTimestamp && timeLabel ? ' · ' : ''}modifié` : ''}
        </Typography>
      ) : null}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {canEdit ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setEditText(message.content);
              setEditing(true);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Modifier
          </MenuItem>
        ) : null}
        {canDelete ? (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setConfirmDeleteOpen(true);
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            Supprimer
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog open={confirmDeleteOpen} onClose={() => !messageActionsPending && setConfirmDeleteOpen(false)}>
        <DialogTitle>Supprimer le message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ce message sera masqué pour les deux participants. Continuer ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={messageActionsPending}>
            Annuler
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={messageActionsPending}
            onClick={async () => {
              if (!onDeleteMessage) return;
              await onDeleteMessage(message.id);
              setConfirmDeleteOpen(false);
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function ConversationMessageList({
  messages,
  theme,
  deliveryPricing,
  deliveryLocationProviderId,
  foundObjectStatus,
  onMarkInTransit,
  onMarkToShip,
  statusActionPending,
  onEditMessage,
  onDeleteMessage,
  messageActionsPending,
}: {
  messages: ConversationMessage[];
  theme: ConversationTheme;
  deliveryPricing?: DeliveryPricingResponse | null;
  deliveryLocationProviderId?: number;
  foundObjectStatus?: FoundObjectStatus | null;
  onMarkInTransit?: () => Promise<void> | void;
  onMarkToShip?: () => Promise<void> | void;
  statusActionPending?: boolean;
  onEditMessage?: (messageId: number, content: string) => Promise<void> | void;
  onDeleteMessage?: (messageId: number) => Promise<void> | void;
  messageActionsPending?: boolean;
}) {
  return (
    <>
      {messages.map((message, index) => {
        const dayKey = messageDayKey(message.created_at);
        const prevDayKey = index > 0 ? messageDayKey(messages[index - 1]?.created_at) : null;
        const showDaySeparator = Boolean(dayKey && dayKey !== prevDayKey);

        return (
          <React.Fragment key={message.id}>
            {showDaySeparator && dayKey ? (
              <MessageDaySeparator label={formatDaySeparatorLabel(dayKey)} />
            ) : null}
            <ConversationMessageBubble
              message={message}
              theme={theme}
              groupPosition={getMessageGroupPosition(messages, index)}
              deliveryPricing={deliveryPricing}
              deliveryLocationProviderId={deliveryLocationProviderId}
              foundObjectStatus={foundObjectStatus}
              onMarkInTransit={onMarkInTransit}
              onMarkToShip={onMarkToShip}
              statusActionPending={statusActionPending}
              onEditMessage={onEditMessage}
              onDeleteMessage={onDeleteMessage}
              messageActionsPending={messageActionsPending}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

export function ConversationAvatar({
  conversationId,
  imageSrc,
  initial,
}: {
  conversationId: number;
  imageSrc?: string | null;
  initial?: string | null;
}) {
  const theme = getConversationTheme(conversationId);
  const letter = (initial || '?').trim().charAt(0).toUpperCase() || '?';

  return (
    <Avatar
      src={imageSrc || undefined}
      sx={{
        bgcolor: theme.avatarBg,
        color: theme.avatarText,
        fontWeight: 700,
        ...(imageSrc
          ? {
              border: `2px solid ${theme.accent}`,
            }
          : {}),
      }}
    >
      {letter}
    </Avatar>
  );
}

export function getConversationListButtonSx(conversationId: number, selected: boolean) {
  const theme = getConversationTheme(conversationId);
  return {
    ...(conversationListItemSx(theme, selected) ?? {}),
    '&.Mui-selected': {
      ...conversationListItemSx(theme, true),
    },
  };
}
